import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Building2, Link2, ShieldCheck, Trash2 } from "lucide-react";

/**
 * Página pessoal de contas vinculadas (sem gate de permissão de propósito —
 * qualquer usuário logado gerencia os próprios vínculos; mesmo precedente do
 * mfaRouter, que também é uma superfície de segurança pessoal sem RBAC).
 *
 * Vincular exige a SUA senha atual (step-up: sessão aberta não basta) e
 * e-mail + senha da outra conta; se ela tiver verificação em duas etapas, o
 * código TOTP também. O servidor aplica rate limit e nunca revela se um
 * e-mail existe.
 */
export default function LinkedAccounts() {
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.accountLinks.list.useQuery();

  const [currentPassword, setCurrentPassword] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  // Conta aguardando confirmação de desvínculo no AlertDialog.
  const [unlinkTarget, setUnlinkTarget] = useState<{ userId: number; email: string | null } | null>(null);

  const linkMut = trpc.accountLinks.link.useMutation({
    onSuccess: () => {
      utils.accountLinks.list.invalidate();
      setCurrentPassword("");
      setEmail("");
      setPassword("");
      setTotpCode("");
      setNeedsTotp(false);
      toast.success("Conta vinculada! Agora você pode trocar de workspace pelo menu lateral.");
    },
    onError: (err) => {
      if (err.message === "TOTP_REQUIRED") {
        setNeedsTotp(true);
        toast.info("Essa conta tem verificação em duas etapas — informe o código do aplicativo.");
        return;
      }
      toast.error(toUserFriendlyError(err));
    },
  });

  const unlinkMut = trpc.accountLinks.unlink.useMutation({
    onSuccess: () => {
      utils.accountLinks.list.invalidate();
      toast.success("Conta desvinculada.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const accounts = data?.accounts ?? [];

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!currentPassword || !email || !password) return;
    linkMut.mutate({
      currentPassword,
      email,
      password,
      totpCode: needsTotp && totpCode ? totpCode : undefined,
    });
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4">
        <div>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" />
            Contas vinculadas
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Se você tem contas em mais de um workspace (por exemplo, em duas
            empresas), vincule-as aqui para trocar entre elas em um clique,
            sem sair e entrar de novo. Cada conta mantém as próprias
            permissões — vincular não dá acesso a nada além do que cada uma
            já vê.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Suas contas</CardTitle>
            <CardDescription>
              Contas do mesmo grupo de vínculo. Qualquer uma delas pode
              desfazer o vínculo.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
            {!isLoading && accounts.length < 2 && (
              <p className="text-sm text-muted-foreground">
                Nenhuma conta vinculada ainda. Use o formulário abaixo para
                vincular a sua conta de outro workspace.
              </p>
            )}
            {accounts.map((account) => (
              <div
                key={account.userId}
                className="flex items-center gap-3 rounded-lg border p-3"
              >
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {account.tenantName ?? "(sem workspace)"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{account.email}</p>
                </div>
                {account.isCurrent && <Badge variant="secondary">conta atual</Badge>}
                {account.blockedReason === "inactive" && (
                  <Badge variant="destructive">desativada</Badge>
                )}
                {account.blockedReason === "workspace" && (
                  <Badge variant="destructive">workspace suspenso</Badge>
                )}
                {account.blockedReason === "superadmin" && (
                  <Badge variant="destructive">indisponível</Badge>
                )}
                {!account.isCurrent && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Desvincular conta"
                    disabled={unlinkMut.isPending}
                    onClick={() =>
                      setUnlinkTarget({ userId: account.userId, email: account.email })
                    }
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Vincular outra conta
            </CardTitle>
            <CardDescription>
              Por segurança, confirme a sua senha atual e informe o e-mail e a
              senha da sua conta no outro workspace. Se alguma das contas foi
              criada com login social e nunca teve senha, defina uma senha
              nela primeiro.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="link-current-password">Sua senha atual</Label>
                <Input
                  id="link-current-password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="link-email">E-mail da outra conta</Label>
                <Input
                  id="link-email"
                  type="email"
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@outraempresa.com.br"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="link-password">Senha da outra conta</Label>
                <Input
                  id="link-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {needsTotp && (
                <div className="space-y-1">
                  <Label htmlFor="link-totp">Código de verificação (TOTP)</Label>
                  <Input
                    id="link-totp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                  />
                </div>
              )}
              <Button
                type="submit"
                disabled={linkMut.isPending || !currentPassword || !email || !password}
              >
                {linkMut.isPending ? "Vinculando…" : "Vincular conta"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <AlertDialog
          open={unlinkTarget !== null}
          onOpenChange={(open) => {
            if (!open) setUnlinkTarget(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Desvincular conta?</AlertDialogTitle>
              <AlertDialogDescription>
                A conta {unlinkTarget?.email ?? ""} deixará de aparecer na troca
                rápida de workspace. Você pode vincular de novo quando quiser,
                informando e-mail e senha.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (unlinkTarget) unlinkMut.mutate({ userId: unlinkTarget.userId });
                }}
              >
                Desvincular
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
